import React, { createContext, useCallback, useContext, useRef, useState } from 'react';

interface GuardCallbacks {
  /** Resolves false when the save did not go through: the caller must then stay put, keeping what was typed. */
  save: () => Promise<void | boolean>;
  discard: () => void;
}

interface NavigationGuardState {
  blocked: boolean;
  callbacks: GuardCallbacks | null;
}

interface NavigationGuardContextValue {
  isBlocked: () => boolean;
  getCallbacks: () => GuardCallbacks | null;
  register: (callbacks: GuardCallbacks) => void;
  unregister: () => void;
  setBlocked: (blocked: boolean) => void;
}

const NavigationGuardContext = createContext<NavigationGuardContextValue>({
  isBlocked: () => false,
  getCallbacks: () => null,
  register: () => {},
  unregister: () => {},
  setBlocked: () => {},
});

export function NavigationGuardProvider({ children }: { children: React.ReactNode }) {
  const stateRef = useRef<NavigationGuardState>({ blocked: false, callbacks: null });
  const [, _forceUpdate] = useState(0);

  const setBlocked = useCallback((blocked: boolean) => {
    stateRef.current.blocked = blocked;
  }, []);

  const register = useCallback((callbacks: GuardCallbacks) => {
    stateRef.current.callbacks = callbacks;
  }, []);

  const unregister = useCallback(() => {
    stateRef.current = { blocked: false, callbacks: null };
  }, []);

  const isBlocked = useCallback(() => stateRef.current.blocked, []);
  const getCallbacks = useCallback(() => stateRef.current.callbacks, []);

  return (
    <NavigationGuardContext.Provider value={{ isBlocked, getCallbacks, register, unregister, setBlocked }}>
      {children}
    </NavigationGuardContext.Provider>
  );
}

export function useNavigationGuard() {
  return useContext(NavigationGuardContext);
}
